import { CancelledError } from "@tanstack/react-query"
import { create, isAxiosError, isCancel } from "axios"
import type { AxiosError, AxiosInstance } from "axios"

import {
  initCaseResponseInterceptor,
  initLanguageRequestInterceptor,
} from "./interceptors"

export interface HttpErrorRecord {
  detail: string
  status: string
  title: string
}

export type HttpErrorAdapter =
  | "axios"
  | "fetch"
  | "none"
  | "openapi"
  | "spraypaint"

export type HttpErrorKind = "connectivity" | "domain"

/**
 * Adapter-level failure provenance attached to a normalized {@link HttpError}.
 *
 * Preserves low-level HTTP-client context so the app can distinguish real
 * connectivity failures from domain errors that happen to travel over HTTP.
 * Classification lives on {@link HttpError.kind}; this object is the evidence
 * used to compute that kind, not the verdict itself.
 */
export interface HttpErrorTransport {
  /** HTTP client that produced the failure (`none` when unknown). */
  adapter: HttpErrorAdapter
  /** True when the request was aborted or canceled by the caller. */
  canceled: boolean
  /** True when the adapter received an HTTP response from the server. */
  hasResponse: boolean
  /**
   * Allowlisted adapter or Node network code (for example `ENOTFOUND` or
   * `ERR_NETWORK`). Omitted when the code is unknown or not allowlisted.
   */
  transportCode?: string
}

interface HttpErrorProps<TError> {
  errors: TError[]
  message: string
  status: number
  transport?: HttpErrorTransport
}

const GATEWAY_UNAVAILABLE_STATUSES: ReadonlySet<number> = new Set([
  502, 503, 504,
])

const CONNECTIVITY_TRANSPORT_CODES: ReadonlySet<string> = new Set([
  "EAI_AGAIN",
  "ECONNABORTED",
  "ECONNREFUSED",
  "ECONNRESET",
  "ENOTFOUND",
  "ERR_CONNECTION_REFUSED",
  "ERR_CONNECTION_RESET",
  "ERR_CONNECTION_TIMED_OUT",
  "ERR_INTERNET_DISCONNECTED",
  "ERR_NAME_NOT_RESOLVED",
  "ERR_NETWORK",
  "ETIMEDOUT",
])

const FETCH_NETWORK_MESSAGES: ReadonlySet<string> = new Set([
  "failed to fetch",
  "load failed",
  "network request failed",
  "networkerror when attempting to fetch resource",
])

const DEFAULT_TRANSPORT: HttpErrorTransport = {
  adapter: "none",
  canceled: false,
  hasResponse: false,
}

const getAllowlistedTransportCode = (code: unknown): string | undefined => {
  if (typeof code !== "string") {
    return undefined
  }

  if (CONNECTIVITY_TRANSPORT_CODES.has(code) || code === "ERR_CANCELED") {
    return code
  }

  return undefined
}

const getNoResponseTransportCode = (
  hasResponse: boolean,
  canceled: boolean
): string | undefined => (!hasResponse && !canceled ? "ERR_NETWORK" : undefined)

const classifyHttpErrorKind = (
  status: number,
  transport: HttpErrorTransport
): HttpErrorKind => {
  if (transport.canceled || status === 401) {
    return "domain"
  }

  if (transport.hasResponse && GATEWAY_UNAVAILABLE_STATUSES.has(status)) {
    return "connectivity"
  }

  if (
    !transport.hasResponse &&
    transport.transportCode !== undefined &&
    CONNECTIVITY_TRANSPORT_CODES.has(transport.transportCode)
  ) {
    return "connectivity"
  }

  return "domain"
}

export class HttpError<TError = Record<string, unknown>> extends Error {
  errors: TError[] = []
  kind: HttpErrorKind = "domain"
  status = 0
  transport: HttpErrorTransport = DEFAULT_TRANSPORT

  constructor({ errors, message, status, transport }: HttpErrorProps<TError>) {
    super(message)
    const resolvedTransport = transport ?? DEFAULT_TRANSPORT
    Object.assign(this, {
      errors,
      kind: classifyHttpErrorKind(status, resolvedTransport),
      status,
      transport: resolvedTransport,
    })
  }
}

const isCanceledRequestError = (error: unknown): boolean => {
  if (error instanceof HttpError) {
    return error.transport.canceled
  }

  if (isCancel(error) || error instanceof CancelledError) {
    return true
  }

  if (isAxiosError(error) && error.code === "ERR_CANCELED") {
    return true
  }

  return (
    error instanceof Error &&
    (error.name === "AbortError" || error.name === "CanceledError")
  )
}

const isFetchNetworkTypeError = (error: unknown): error is TypeError =>
  error instanceof TypeError &&
  FETCH_NETWORK_MESSAGES.has(error.message.toLowerCase())

interface HttpErrorResponse<TError> {
  detail?: unknown
  errors?: TError[]
}

const UNKNOWN_ERROR_MESSAGE = "Unknown error"
const SPRAYPAINT_INVALID_JSON_MESSAGE = "invalid json"

/** Omit nullish, empty, and whitespace-only strings so Error Tracking never fingerprints as `Error: `. */
const omitBlankString = (
  value: string | null | undefined
): string | undefined => {
  if (typeof value !== "string") {
    return undefined
  }

  return value.trim() !== "" ? value : undefined
}

const getStatusFallbackMessage = (status: number): string | undefined =>
  status > 0 ? `HTTP ${status}` : undefined

const resolveHttpErrorMessage = (
  status: number,
  ...candidates: (string | null | undefined)[]
): string => {
  for (const candidate of candidates) {
    const message = omitBlankString(candidate)
    if (message) {
      return message
    }
  }

  return getStatusFallbackMessage(status) ?? UNKNOWN_ERROR_MESSAGE
}

const isValidationDetailItem = (value: unknown): value is { msg: string } =>
  typeof value === "object" &&
  value !== null &&
  "msg" in value &&
  typeof (value as { msg: unknown }).msg === "string"

const getDetailMessage = (detail: unknown): string | undefined => {
  if (typeof detail === "string") {
    return omitBlankString(detail)
  }

  if (Array.isArray(detail)) {
    if (detail.length === 0) {
      return undefined
    }

    const firstDetail: unknown = detail[0]
    if (isValidationDetailItem(firstDetail)) {
      return omitBlankString(firstDetail.msg)
    }

    return undefined
  }

  if (
    detail &&
    typeof detail === "object" &&
    "message" in detail &&
    typeof (detail as { message: unknown }).message === "string"
  ) {
    return omitBlankString((detail as { message: string }).message)
  }

  return undefined
}

export const fromAxiosError = <TError = Record<string, unknown>>(
  error: AxiosError<HttpErrorResponse<TError>>
): HttpError<TError> => {
  const data = error.response?.data
  const status = error.response?.status ?? 0
  const canceled = isCanceledRequestError(error)
  const hasResponse = error.response != null
  const transportCode =
    getAllowlistedTransportCode(error.code) ??
    (error.request != null
      ? getNoResponseTransportCode(hasResponse, canceled)
      : undefined)
  // Prefer Core API / FastAPI `detail` (string or validation array) when present.
  const detailMessage = getDetailMessage(data?.detail)

  return new HttpError({
    errors: data?.errors ?? [],
    message: resolveHttpErrorMessage(
      status,
      detailMessage,
      error.response?.statusText,
      error.message
    ),
    status,
    transport: {
      adapter: "axios",
      canceled,
      hasResponse,
      transportCode,
    },
  })
}

export interface OpenApiErrorLike {
  body: unknown
  message: string
  status: number
}

export const fromOpenApiError = (error: OpenApiErrorLike): HttpError => {
  const body = error.body as Record<string, unknown> | null
  const hasResponse = error.status > 0
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return new HttpError({
    errors: Array.isArray(body?.errors) ? body.errors : [],
    message: resolveHttpErrorMessage(error.status, error.message),
    status: error.status,
    transport: {
      adapter: "openapi",
      canceled: false,
      hasResponse,
      transportCode: getNoResponseTransportCode(hasResponse, false),
    },
  })
}

const isOpenApiErrorLike = (error: unknown): error is OpenApiErrorLike =>
  typeof error === "object" &&
  error !== null &&
  "message" in error &&
  "status" in error &&
  "body" in error &&
  typeof (error as OpenApiErrorLike).message === "string" &&
  typeof (error as OpenApiErrorLike).status === "number"

const getErrorMessageFromRecord = (
  record: Record<string, unknown>
): string | undefined =>
  omitBlankString(
    typeof record.message === "string" ? record.message : undefined
  ) ??
  omitBlankString(typeof record.title === "string" ? record.title : undefined)

const getJsonApiErrorMessage = (errors: unknown[]): string | undefined => {
  const firstError = errors[0]
  if (!firstError || typeof firstError !== "object") {
    return undefined
  }

  const firstErrorRecord = firstError as Record<string, unknown>

  return (
    omitBlankString(
      typeof firstErrorRecord.detail === "string"
        ? firstErrorRecord.detail
        : undefined
    ) ?? getErrorMessageFromRecord(firstErrorRecord)
  )
}

const getErrorStatusFromRecord = (
  record: Record<string, unknown>,
  fallbackStatus: number
): number => {
  if (typeof record.status === "number") {
    return record.status
  }

  return fallbackStatus
}

const httpErrorFromUnknownOpenApiBody = (
  body: unknown,
  status: number,
  transport?: HttpErrorTransport
): HttpError => {
  if (body && typeof body === "object") {
    const record = body as Record<string, unknown>
    const detailMessage = getDetailMessage(record.detail)
    const errors = Array.isArray(record.errors) ? record.errors : []
    const resolvedStatus =
      status > 0 ? status : getErrorStatusFromRecord(record, status)

    return new HttpError({
      errors: errors as Record<string, unknown>[],
      message: resolveHttpErrorMessage(
        resolvedStatus,
        detailMessage,
        getErrorMessageFromRecord(record)
      ),
      status: resolvedStatus,
      transport,
    })
  }

  if (typeof body === "string") {
    return new HttpError({
      errors: [],
      message: resolveHttpErrorMessage(status, body),
      status,
      transport,
    })
  }

  return new HttpError({
    errors: [],
    message: resolveHttpErrorMessage(status),
    status,
    transport,
  })
}

interface SpraypaintResponseLike {
  body?: unknown
  data?: unknown
  json?: unknown
  status?: unknown
  statusText?: unknown
}

interface SpraypaintResponseErrorLike extends Error {
  body?: unknown
  data?: unknown
  json?: unknown
  payload?: unknown
  response?: SpraypaintResponseLike
}

const isSpraypaintResponseErrorLike = (
  error: unknown
): error is SpraypaintResponseErrorLike =>
  error instanceof Error &&
  typeof error === "object" &&
  error !== null &&
  "response" in error &&
  typeof (error as { response?: unknown }).response === "object" &&
  (error as { response?: unknown }).response !== null &&
  !Array.isArray((error as { response?: unknown }).response)

const getSpraypaintResponseStatus = (
  response: SpraypaintResponseLike | undefined,
  fallbackStatus: number
): number =>
  typeof response?.status === "number" ? response.status : fallbackStatus

const getSpraypaintResponseBody = (
  error: SpraypaintResponseErrorLike
): unknown => {
  if (error.payload !== undefined) {
    return error.payload
  }

  if (error.body !== undefined) {
    return error.body
  }

  if (error.data !== undefined) {
    return error.data
  }

  if (error.json !== undefined) {
    return error.json
  }

  if (error.response?.body !== undefined) {
    return error.response.body
  }

  if (error.response?.data !== undefined) {
    return error.response.data
  }

  if (error.response?.json !== undefined) {
    return error.response.json
  }

  return undefined
}

const fromSpraypaintResponseError = (
  error: SpraypaintResponseErrorLike,
  fallbackStatus: number
): HttpError => {
  const status = getSpraypaintResponseStatus(error.response, fallbackStatus)
  const responseBody = getSpraypaintResponseBody(error)
  const responseRecord =
    responseBody && typeof responseBody === "object"
      ? (responseBody as Record<string, unknown>)
      : null
  const errors = Array.isArray(responseRecord?.errors)
    ? responseRecord.errors
    : []
  const jsonApiMessage = getJsonApiErrorMessage(errors)
  const statusText =
    typeof error.response?.statusText === "string"
      ? error.response.statusText
      : undefined
  const errorMessage = omitBlankString(error.message)
  const isSpraypaintInvalidJsonMessage =
    errorMessage?.toLowerCase() === SPRAYPAINT_INVALID_JSON_MESSAGE
  let parseFailureMessage: string | undefined

  if (isSpraypaintInvalidJsonMessage) {
    parseFailureMessage =
      status > 0
        ? `Invalid JSON response (HTTP ${status})`
        : "Invalid JSON response"
  }

  const canceled = isCanceledRequestError(error)
  const hasResponse = typeof error.response?.status === "number" && status > 0

  return new HttpError({
    errors: errors as Record<string, unknown>[],
    message: resolveHttpErrorMessage(
      status,
      jsonApiMessage,
      parseFailureMessage,
      getErrorMessageFromRecord(responseRecord ?? {}),
      statusText,
      errorMessage
    ),
    status,
    transport: {
      adapter: "spraypaint",
      canceled,
      hasResponse,
      transportCode: getNoResponseTransportCode(hasResponse, canceled),
    },
  })
}

export const toHttpError = (
  error: unknown,
  responseStatus = 0,
  transportHint?: HttpErrorTransport
): HttpError => {
  if (error instanceof HttpError) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return error
  }
  if (isAxiosError(error)) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return fromAxiosError(error)
  }
  if (isCanceledRequestError(error)) {
    return new HttpError({
      errors: [],
      message: resolveHttpErrorMessage(
        responseStatus,
        error instanceof Error ? error.message : undefined
      ),
      status: responseStatus,
      transport: transportHint ?? {
        adapter: "none",
        canceled: true,
        hasResponse: false,
      },
    })
  }
  if (isOpenApiErrorLike(error)) {
    return fromOpenApiError(error)
  }
  if (isSpraypaintResponseErrorLike(error)) {
    return fromSpraypaintResponseError(error, responseStatus)
  }
  if (isFetchNetworkTypeError(error)) {
    return new HttpError({
      errors: [],
      message: resolveHttpErrorMessage(responseStatus, error.message),
      status: responseStatus,
      transport: transportHint ?? {
        adapter: "fetch",
        canceled: false,
        hasResponse: false,
        transportCode: getNoResponseTransportCode(false, false),
      },
    })
  }
  if (error instanceof Error) {
    return new HttpError({
      errors: [],
      message: resolveHttpErrorMessage(responseStatus, error.message),
      status: responseStatus,
      transport: transportHint,
    })
  }
  return httpErrorFromUnknownOpenApiBody(error, responseStatus, transportHint)
}

export const isConnectivityError = (error: unknown): boolean => {
  if (error instanceof HttpError) {
    return error.kind === "connectivity"
  }

  return toHttpError(error).kind === "connectivity"
}

export async function fetchOrThrow<T>(
  fetcher: () => Promise<{
    data?: T
    error?: unknown
    response?: {
      status: number
    }
  }>
): Promise<T> {
  const { data, error, response } = await fetcher()
  if (data !== undefined) {
    return data
  }
  const status = response?.status ?? 0
  const canceled = isCanceledRequestError(error)
  const hasResponse = Boolean(response && status > 0)
  throw toHttpError(error, status, {
    adapter: "openapi",
    canceled,
    hasResponse,
    transportCode: getNoResponseTransportCode(hasResponse, canceled),
  })
}

export interface SuccessData {
  success: boolean
}

export interface ErrorData extends SuccessData {
  error: string
  success: boolean
}

export const httpClient: AxiosInstance = create({
  baseURL: process.env.REACT_APP_API_BASE_URL ?? "",
})

// Set up interceptors on the Axios instance
initCaseResponseInterceptor(httpClient)
initLanguageRequestInterceptor(httpClient)
